import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /** @type {number|null} */
  #restoreFocusToLine = null;

  /**
   * After a line is removed, move focus to the next line's remove button, the previous
   * one if it was the last line, or the cart heading/section when the cart is empty.
   */
  #restoreFocusAfterRemove() {
    const line = this.#restoreFocusToLine;
    this.#restoreFocusToLine = null;
    if (line === null) return;
    // Wait for the row removal animation / morph to settle.
    setTimeout(() => {
      const section = document.getElementById(`shopify-section-${this.sectionId}`) || this;
      const buttons = [...section.querySelectorAll('.cart-items__remove')].filter((b) => b.isConnected && b.offsetParent !== null);
      const target =
        buttons[Math.min(line - 1, buttons.length - 1)] ||
        section.querySelector('h1, h2, .cart-drawer__heading, [tabindex="-1"]') ||
        section;
      if (!target.matches('a[href], button, input, select, textarea, [tabindex]')) target.setAttribute('tabindex', '-1');
      target.focus();
    }, 450);
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    // pointer-events:none on .cart-items-disabled blocks mouse but not
    // keyboard activation; a second remove while one is in flight targets a
    // reindexed line and deletes the wrong item (and racing morphs corrupt
    // the DOM), so drop repeat activations until the update settles.
    if (this.classList.contains('cart-items-disabled')) return;

    // Remember where keyboard focus was so it can be restored after the section
    // re-renders; otherwise focus falls back to <body> (WCAG 2.4.3).
    this.#restoreFocusToLine = this.contains(document.activeElement) ? line : null;

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections?.[this.sectionId] ?? '',
          'text/html'
        );

        // The cart's own item count; the hidden element in the section markup is the fallback (the markup
        // can be missing, see #renderFromSections)
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount =
          typeof parsedResponseText.item_count === 'number'
            ? parsedResponseText.item_count
            : newCartHiddenItemCount
              ? parseInt(newCartHiddenItemCount, 10)
              : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        return this.#renderFromSections(parsedResponseText.sections).then(() => this.#restoreFocusAfterRemove());
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    this.#renderFromSections(event.detail.data.sections);
  };

  /**
   * Morphs this section with its markup from a cart API response (`sections`), or re-renders it when the
   * response has none.
   *
   * @param {Record<string, string | null> | undefined} sections - The response's rendered sections.
   * @returns {Promise<void>}
   */
  async #renderFromSections(sections) {
    const html = sections?.[this.sectionId];
    if (html) {
      try {
        await morphSection(this.sectionId, html);
        return;
      } catch (error) {
        console.error(error);
      }
    }
    await this.#rerender();
  }

  /**
   * Re-renders this section with the Section Rendering API.
   *
   * A cart response comes back with `null` for this section when the request was served by a different
   * theme than the one that rendered this page: another tab opened a different theme preview (the preview
   * theme is per browser, not per tab), or a theme was published while this page was open. Section ids of
   * section groups (the drawer lives in the header group) differ between themes, so that theme cannot
   * render this page's drawer, and a plain re-render fails the same way (404). The drawer then kept the
   * markup it had when the page loaded, so it opened on "Your cart is empty" with the item in the cart.
   * A theme preview re-renders with its own theme instead (as reopening its preview link would), and as a
   * last resort the cart page shows the real cart.
   *
   * @returns {Promise<void>}
   */
  async #rerender() {
    try {
      const html = await sectionRenderer.getSectionHTML(this.sectionId, false);
      await morphSection(this.sectionId, html);
      return;
    } catch (error) {
      console.error(error);
    }

    const theme = window.Shopify?.theme;
    if (theme?.id && theme.role !== 'main') {
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('preview_theme_id', String(theme.id));
        const html = await sectionRenderer.getSectionHTML(this.sectionId, false, url);
        await morphSection(this.sectionId, html);
        return;
      } catch (error) {
        console.error(error);
      }
    }

    window.location.assign(Theme.routes.cart_url);
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
